<template>
    <UModal
        v-model:open="open"
        :title="$t('wizard.title')"
        :ui="{ content: 'max-w-2xl' }"
        @after:leave="reset"
    >
        <template #body>
            <div class="flex flex-col gap-6">
                <UStepper ref="stepper" v-model="step" :items="stepItems" disabled />

                <UAlert v-if="errorMessage" color="error" variant="soft" icon="i-lucide-circle-alert"
                        :title="errorMessage" />

                <UForm ref="wizardForm" :state="form" :schema="stepSchema" class="flex flex-col gap-6" @submit="onStepSubmit">
                <!-- Step 0: Podatki -->
                <div v-show="step === 0" class="flex flex-col gap-4">
                    <div class="rounded-lg border border-default p-3 flex items-center gap-3">
                        <UAvatar :text="operatorInitials" size="sm" />
                        <div class="text-sm">
                            <p class="font-medium text-default">{{ operatorName }}</p>
                            <p class="text-muted text-xs">{{ appStore.userData?.email }}</p>
                        </div>
                    </div>
                    <!-- v1.16: every reservation belongs to a group -->
                    <UAlert v-if="groupOptions.length === 0" color="warning" variant="soft" icon="i-lucide-users-round"
                            :title="$t('wizard.no_groups_title')" :description="$t('wizard.no_groups_desc')" />
                    <UFormField :label="$t('wizard.group')" name="group_id" required>
                        <USelectMenu v-model="form.group_id" :items="groupOptions" value-key="value"
                                     :search-input="{ placeholder: $t('general.search_hint') }"
                                     :placeholder="$t('wizard.group_placeholder')" class="w-full" />
                    </UFormField>
                    <UFormField :label="$t('wizard.project')">
                        <USelectMenu v-model="form.project_id" :items="projectOptions" value-key="value"
                                     :search-input="{ placeholder: $t('general.search_hint') }"
                                     :placeholder="$t('wizard.project_placeholder')" class="w-full" />
                    </UFormField>
                    <UFormField v-if="form.project_id === CUSTOM_VALUE" :label="$t('wizard.project_custom')"
                                name="custom_project" required>
                        <UInput v-model="form.custom_project" class="w-full"
                                :placeholder="$t('wizard.project_custom_placeholder')" />
                    </UFormField>
                    <UFormField :label="$t('wizard.note')" name="note">
                        <UTextarea v-model="form.note" class="w-full" :rows="2"
                                   :placeholder="$t('wizard.note_placeholder')" />
                    </UFormField>
                    <UFormField :label="$t('wizard.device')" name="device_id" required>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <button
                                v-for="d in activeDevices"
                                :key="d.id"
                                type="button"
                                class="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors"
                                :class="form.device_id === d.id ? 'border-primary bg-primary/5' : 'border-default hover:bg-elevated'"
                                @click="selectDevice(d)"
                            >
                                <span class="size-3 rounded-full shrink-0" :style="{ backgroundColor: d.color }" />
                                <div class="min-w-0">
                                    <p class="text-sm font-medium text-default truncate">{{ d.name }}</p>
                                    <p class="text-xs text-muted">
                                        {{ $t('device.interval_min', { min: d.slot_minutes }) }}
                                        <template v-if="d.current_probe_name"> · {{ d.current_probe_name }}</template>
                                    </p>
                                </div>
                            </button>
                        </div>
                    </UFormField>
                </div>

                <!-- Step 1: Termin -->
                <div v-show="step === 1" class="flex flex-col gap-4">
                    <UAlert
                        :color="quotaRemaining === null || quotaRemaining > 0 ? 'success' : 'error'"
                        variant="soft"
                        :icon="quotaRemaining === null || quotaRemaining > 0 ? 'i-lucide-circle-check' : 'i-lucide-circle-alert'"
                    >
                        <template #description>
                            <span v-if="quotaAllocated !== null">
                                {{ $t('wizard.quota_available', {
                                    device: selectedDevice?.name,
                                    remaining: quotaRemaining,
                                    allocated: quotaAllocated,
                                    renewal: quotaRenewalLabel,
                                }) }}
                                <span v-if="!isCurrentWindow && quotaPeriodLabel" class="block mt-0.5 text-xs opacity-80">
                                    {{ $t('wizard.quota_window', { period: quotaPeriodLabel }) }}
                                </span>
                            </span>
                            <span v-else>{{ $t('wizard.quota_none') }}</span>
                        </template>
                    </UAlert>

                    <div class="rounded-lg border border-default p-3">
                        <USwitch v-model="multiDay" :label="$t('wizard.multi_day')" />
                        <p v-if="multiDay" class="mt-1.5 text-xs text-muted">{{ $t('wizard.multi_day_hint') }}</p>
                    </div>

                    <!-- Single day: one grid, click start then end to span a range -->
                    <template v-if="!multiDay">
                        <div class="flex flex-wrap items-center gap-3">
                            <UPopover>
                                <UButton icon="i-lucide-calendar" color="neutral" variant="outline" :label="dateLabel" />
                                <template #content>
                                    <UCalendar v-bind="calendarProps" class="p-2" @update:model-value="onCalendarChange" />
                                </template>
                            </UPopover>
                            <UButton v-if="lastFreeStart" variant="link" size="sm"
                                     :label="$t('wizard.last_free')" @click="selectSlot(lastFreeStart)" />
                        </div>

                        <UFormField name="start">
                            <SlotGrid :slots="slots" :loading="slotsLoading"
                                      :selected-start="form.start" :selected-end="form.end"
                                      @select="(s: ISlot) => selectSlot(s.start)" />
                        </UFormField>
                    </template>

                    <!-- Multi-day: start day + slot on the left, end day + slot on the right -->
                    <UFormField v-else name="start">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div class="flex flex-col gap-2">
                                <p class="text-xs font-medium text-dimmed">{{ $t('wizard.start_day') }}</p>
                                <UPopover>
                                    <UButton icon="i-lucide-calendar" color="neutral" variant="outline"
                                             :label="dateLabel" class="w-full justify-start" />
                                    <template #content>
                                        <UCalendar v-bind="calendarProps" class="p-2" @update:model-value="onCalendarChange" />
                                    </template>
                                </UPopover>
                                <SlotGrid :slots="slots" :loading="slotsLoading" dense
                                          :selected-start="form.start" :selected-end="form.end"
                                          @select="selectSpanStart" />
                            </div>
                            <div class="flex flex-col gap-2">
                                <p class="text-xs font-medium text-dimmed">{{ $t('wizard.end_day') }}</p>
                                <UPopover>
                                    <UButton icon="i-lucide-calendar" color="neutral" variant="outline"
                                             :label="endDateLabel" class="w-full justify-start" />
                                    <template #content>
                                        <UCalendar v-bind="endCalendarProps" class="p-2" @update:model-value="onEndCalendarChange" />
                                    </template>
                                </UPopover>
                                <SlotGrid :slots="endSlots" :loading="endSlotsLoading" dense
                                          :selected-start="form.start" :selected-end="form.end"
                                          @select="selectSpanEnd" />
                            </div>
                        </div>
                    </UFormField>

                    <div class="flex flex-wrap items-center gap-4 text-xs text-muted">
                        <span class="flex items-center gap-1.5">
                            <span class="inline-block size-3 rounded-sm border border-default border-l-2 border-l-primary" />
                            {{ $t('wizard.legend_quota') }}
                        </span>
                        <span class="flex items-center gap-1.5">
                            <span class="inline-block size-3 rounded-sm border border-default" />
                            {{ $t('wizard.legend_free') }}
                        </span>
                    </div>

                    <UAlert v-if="selectedOvernightUntil" color="info" variant="soft" icon="i-lucide-moon"
                            :title="$t('wizard.overnight_title')"
                            :description="$t('wizard.overnight_notice', { time: selectedOvernightUntil })" />

                    <p class="text-xs text-muted flex items-center gap-1.5">
                        <UIcon name="i-lucide-info" class="size-3.5 shrink-0" />
                        {{ $t('wizard.slot_help') }}
                    </p>

                    <UAlert v-if="outsideWindow" color="info" variant="soft" icon="i-lucide-calendar-clock"
                            :title="$t('wizard.window_notice', { days: freeWindowDays })" />

                    <UAlert v-if="spanConflict" color="warning" variant="soft" icon="i-lucide-triangle-alert"
                            :title="$t('wizard.span_conflict')" />
                    <UAlert v-else-if="previewTruncated" color="info" variant="soft" icon="i-lucide-info"
                            :title="$t('wizard.span_preview_limited', { days: MAX_PREVIEW_DAYS })" />

                    <div v-if="form.start && form.end" class="rounded-lg bg-elevated p-3 text-sm flex flex-col gap-1.5">
                        <div class="flex items-center justify-between gap-2">
                            <span v-if="spansDays" class="text-default">
                                {{ formatDateTime(form.start) }} → {{ formatDateTime(form.end) }}
                            </span>
                            <span v-else class="text-default">{{ formatDate(form.start) }} · {{ formatTimeRange(form.start, form.end) }}</span>
                            <UBadge :color="overQuota ? 'error' : previewTruncated ? 'neutral' : 'info'" variant="soft"
                                    :label="previewTruncated
                                        ? $t('wizard.slot_uses_partial', { n: selectedCost })
                                        : $t('wizard.slot_uses', { n: selectedCost })" />
                        </div>
                        <p v-if="overQuota" class="flex items-center gap-1 text-xs text-error">
                            <UIcon name="i-lucide-circle-alert" class="size-3.5 shrink-0" />
                            {{ $t('wizard.quota_over') }}
                        </p>
                    </div>

                    <div class="rounded-lg border border-default p-3">
                        <USwitch v-model="form.recurrenceEnabled" :label="$t('wizard.recurrence')" />
                        <div v-if="form.recurrenceEnabled" class="grid grid-cols-2 gap-3 mt-3">
                            <UFormField :label="$t('wizard.frequency')">
                                <USelect v-model="form.recType" :items="recurrenceOptions()" value-key="value" class="w-full" />
                            </UFormField>
                            <UFormField :label="$t('wizard.occurrences')">
                                <UInputNumber v-model="form.recCount" :min="1" :max="52" class="w-full" />
                            </UFormField>
                        </div>
                    </div>
                </div>

                <!-- Step 2: Sonda -->
                <div v-show="step === 2" class="flex flex-col gap-4">
                    <div class="rounded-lg border p-3 flex items-center gap-3"
                         :style="{ borderColor: 'var(--ui-info)' }">
                        <UIcon name="i-lucide-magnet" class="size-5 text-info shrink-0" />
                        <p class="text-sm text-default">
                            <template v-if="effectiveProbe.probe_id">
                                {{ $t('wizard.effective_probe', { probe: effectiveProbe.probe_name }) }}
                            </template>
                            <template v-else>{{ $t('wizard.effective_none') }}</template>
                        </p>
                    </div>

                    <UFormField name="probe_id">
                        <URadioGroup v-model="form.probe_id" :items="probeRadioItems" value-key="value">
                            <template #label="{ item }">
                                <span class="flex items-center gap-2">
                                    {{ item.label }}
                                    <UBadge v-if="item.value === effectiveProbe.probe_id" size="sm" color="info" variant="soft"
                                            :label="$t('wizard.probe_effective_badge')" />
                                </span>
                            </template>
                        </URadioGroup>
                        <p v-if="compatibleProbes.length === 0" class="text-sm text-muted">{{ $t('wizard.no_compatible_probes') }}</p>
                    </UFormField>

                    <UAlert v-if="probeRequiresConfirm" color="warning" variant="soft" icon="i-lucide-clock"
                            :title="$t('wizard.probe_pending_notice')" />
                </div>

                <!-- Step 3: Pregled -->
                <div v-show="step === 3" class="flex flex-col gap-3">
                    <p class="text-sm text-muted">{{ $t('wizard.review_intro') }}</p>
                    <dl class="divide-y divide-default rounded-lg border border-default">
                        <ReviewRow :label="$t('wizard.operator')" :value="operatorName" />
                        <ReviewRow :label="$t('wizard.group')" :value="selectedGroupName" />
                        <ReviewRow :label="$t('wizard.project')" :value="reviewProject || $t('wizard.project_placeholder')" />
                        <ReviewRow :label="$t('wizard.device')" :value="selectedDevice?.name || ''" />
                        <template v-if="spansDays">
                            <ReviewRow :label="$t('general.from')" :value="formatDateTime(form.start)" />
                            <ReviewRow :label="$t('general.to')" :value="formatDateTime(form.end)" />
                        </template>
                        <template v-else>
                            <ReviewRow :label="$t('general.date')" :value="form.start ? formatDate(form.start) : ''" />
                            <ReviewRow :label="$t('general.time')" :value="form.start ? formatTimeRange(form.start, form.end) : ''" />
                        </template>
                        <div class="flex items-center justify-between gap-4 px-4 py-2.5">
                            <dt class="text-sm text-muted">{{ $t('wizard.step_probe') }}</dt>
                            <dd class="flex items-center gap-2 text-sm font-medium text-default text-right">
                                {{ reviewProbe || '—' }}
                                <UBadge v-if="probeRequiresConfirm" size="sm" color="warning" variant="soft"
                                        :label="$t('wizard.needs_confirm')" />
                            </dd>
                        </div>
                        <div v-if="selectedOvernightUntil" class="flex items-center justify-between gap-4 px-4 py-2.5">
                            <dt class="text-sm text-muted">{{ $t('wizard.overnight_title') }}</dt>
                            <dd class="flex items-center gap-1.5 text-sm font-medium text-default text-right">
                                <UIcon name="i-lucide-moon" class="size-3.5 shrink-0" />
                                {{ $t('wizard.overnight_short', { time: selectedOvernightUntil }) }}
                            </dd>
                        </div>
                        <ReviewRow :label="$t('wizard.recurrence')" :value="reviewRecurrence" />
                        <ReviewRow v-if="form.note" :label="$t('wizard.note')" :value="form.note" />
                    </dl>
                    <UAlert v-if="selectedOvernightUntil" color="info" variant="soft" icon="i-lucide-moon"
                            :title="$t('wizard.overnight_title')"
                            :description="$t('wizard.overnight_notice', { time: selectedOvernightUntil })" />
                    <UAlert v-if="outsideWindow" color="info" variant="soft" icon="i-lucide-calendar-clock"
                            :title="$t('wizard.window_notice', { days: freeWindowDays })" />
                </div>
                </UForm>
            </div>
        </template>

        <template #footer>
            <div class="flex w-full items-center justify-between gap-2">
                <UButton v-if="step > 0" color="neutral" variant="ghost" icon="i-lucide-arrow-left"
                         :label="$t('general.back')" @click="step--" />
                <div v-else />
                <div class="flex gap-2">
                    <UButton color="neutral" variant="subtle" :label="$t('general.cancel')" @click="open = false" />
                    <UButton v-if="step < 3" :label="$t('general.next')" trailing-icon="i-lucide-arrow-right"
                             @click="wizardForm?.submit()" />
                    <UButton v-else :label="$t('wizard.submit')" :loading="submitting" @click="wizardForm?.submit()" />
                </div>
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { computed, reactive, ref, useTemplateRef, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import * as z from 'zod'

    import ReviewRow from '@/views/reservation/components/ReviewRow.vue'
    import SlotGrid from '@/views/reservation/components/SlotGrid.vue'

    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { useWizardStore } from '@/stores/wizard'
    import { parseError } from '@/libs/error'
    import { PrintSuccess } from '@/libs/toast'
    import { isAdmin as checkAdmin } from '@/libs/auth'
    import { recurrenceOptions, renewalLabel, userInitials, CUSTOM_VALUE, NONE_VALUE } from '@/libs/domain'
    import dayjs, {
        formatDate, formatDateTime, formatTimeRange,
        calendarDateToISO, isoToCalendarDate, todayCalendarDate, TZ,
    } from '@/libs/dayjs'
    import { DeviceStatus } from '@/enums/domain'
    import type { IDevice } from '@/types/Device'
    import type { IGroup } from '@/types/Group'
    import type { IProject } from '@/types/Client'
    import type { IProbe } from '@/types/Probe'
    import type { ISlotsResponse, ISlot } from '@/types/Slot'
    import type { IGroupQuota, IQuotaStatus } from '@/types/Quota'
    import type { IReservationCreateResponse, IEffectiveProbe } from '@/types/Reservation'
    import type { ListResponse, SelectOption } from '@/types/Common'
    import type { DateValue } from '@internationalized/date'

    const { t } = useI18n()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()
    const wizardStore = useWizardStore()
    const { open, prefill } = storeToRefs(wizardStore)

    const step = ref(0)
    const submitting = ref(false)
    const errorMessage = ref('')
    const wizardForm = useTemplateRef<{ submit: () => void }>('wizardForm')

    const stepItems = computed(() => [
        { title: t('wizard.step_data'),   icon: 'i-lucide-clipboard-list' },
        { title: t('wizard.step_slot'),   icon: 'i-lucide-clock' },
        { title: t('wizard.step_probe'),  icon: 'i-lucide-magnet' },
        { title: t('wizard.step_review'), icon: 'i-lucide-check' },
    ])

    const form = reactive({
        group_id:          '',
        project_id:        NONE_VALUE,
        custom_project:    '',
        note:              '',
        device_id:         '',
        start:             '',
        end:               '',
        probe_id:          '',
        recurrenceEnabled: false,
        recType:           'weekly',
        recCount:          4,
    })

    // Effective probe at the selected slot (v1.5) + whether the user hand-picked a probe.
    const effectiveProbe = ref<IEffectiveProbe>({ probe_id: null, probe_name: null })
    const manualProbe = ref(false)

    // --- reference data ---
    const devices = ref<IDevice[]>([])
    const groups = ref<IGroup[]>([])
    const projects = ref<IProject[]>([])
    const probes = ref<IProbe[]>([])

    const activeDevices = computed(() => devices.value.filter((d) => d.status === DeviceStatus.Active))
    const selectedDevice = computed(() => devices.value.find((d) => d.id === form.device_id))

    const isAdmin = computed(() => checkAdmin())
    const availableGroups = computed(() => {
        if (isAdmin.value) return groups.value
        const ids = appStore.userData?.group_ids ?? []
        return groups.value.filter((g) => ids.includes(g.id))
    })
    const groupOptions = computed<SelectOption[]>(() => availableGroups.value.map((g) => ({ label: g.name, value: g.id })))
    // v1.7: either an existing project or free text (custom_project) — never both.
    const projectOptions = computed<SelectOption[]>(() => [
        { label: t('wizard.project_placeholder'), value: NONE_VALUE },
        ...projects.value.map((p) => ({ label: p.name, value: p.id })),
        { label: t('wizard.project_manual'), value: CUSTOM_VALUE },
    ])

    const operatorName = computed(() =>
        appStore.userData ? `${appStore.userData.first_name} ${appStore.userData.last_name}` : '',
    )
    const operatorInitials = computed(() => userInitials(operatorName.value))


    // --- date & slots ---
    const calendarDate = ref<DateValue>(todayCalendarDate())
    const minCalendarDate = computed<DateValue | undefined>(() => (isAdmin.value ? undefined : todayCalendarDate()))
    // v-bind avoids a nominal type-identity mismatch between reka-ui's structural
    // DateValue and @internationalized/date's branded classes.
    const calendarProps = computed((): Record<string, unknown> => ({
        modelValue: calendarDate.value,
        minValue:   minCalendarDate.value,
    }))
    const onCalendarChange = (value: unknown) => {
        if (value) calendarDate.value = value as DateValue
    }
    const selectedDate = computed(() => calendarDateToISO(calendarDate.value))
    const dateLabel = computed(() => (form.start ? formatDate(form.start) : formatDate(selectedDate.value)))

    const slots = ref<ISlot[]>([])
    const slotsLoading = ref(false)

    // --- multi-day span (v1.12): a second day+slot picker for the end of the booking ---
    // Days between the endpoints are fetched only to preview cost and conflicts; beyond
    // MAX_PREVIEW_DAYS we skip the preview and let the server validate on submit.
    const MAX_PREVIEW_DAYS = 14
    // Contract §5.2 caps a reservation span at 30 Ljubljana days.
    const MAX_SPAN_DAYS = 30

    const multiDay = ref(false)
    const endCalendarDate = ref<DateValue>(todayCalendarDate())
    const endSlots = ref<ISlot[]>([])
    const endSlotsLoading = ref(false)
    const middleGrids = ref<Record<string, ISlot[]>>({})

    const endCalendarProps = computed((): Record<string, unknown> => ({
        modelValue: endCalendarDate.value,
        // The end day can never precede the start day, nor exceed the contract's 30-day
        // span cap (api-contract §5.2) — the server rejects anything longer outright.
        minValue:   calendarDate.value,
        maxValue:   calendarDate.value?.add({ days: MAX_SPAN_DAYS - 1 }),
    }))
    const onEndCalendarChange = (value: unknown) => {
        if (value) endCalendarDate.value = value as DateValue
    }
    const endSelectedDate = computed(() => calendarDateToISO(endCalendarDate.value))
    const endDateLabel = computed(() =>
        form.end ? formatDate(form.end) : formatDate(endSelectedDate.value))

    const spanDays = computed(() => {
        if (!multiDay.value || !selectedDate.value || !endSelectedDate.value) return 1
        return dayjs(endSelectedDate.value).diff(dayjs(selectedDate.value), 'day') + 1
    })
    const previewTruncated = computed(() => multiDay.value && spanDays.value > MAX_PREVIEW_DAYS)

    const spansDays = computed(() =>
        !!form.start && !!form.end
        && dayjs(form.start).format('YYYY-MM-DD') !== dayjs(form.end).format('YYYY-MM-DD'),
    )

    const lastFreeStart = computed(() => {
        const free = slots.value.filter((s) => s.available)
        return free.length ? free[free.length - 1].start : ''
    })

    // --- quota (from the slots response — window containing the selected date, v1.3) ---
    const quota = ref<IQuotaStatus | null>(null)
    const quotaAllocated = computed(() => (quota.value ? quota.value.allocated : null))
    const quotaRemaining = computed(() => (quota.value ? quota.value.remaining : null))
    const quotaRenewalLabel = computed(() => (quota.value ? renewalLabel(quota.value.renewal) : ''))

    // Is the selected date's renewal window the current one? (period_to is exclusive)
    const isCurrentWindow = computed(() => {
        if (!quota.value?.period_from || !quota.value?.period_to) return true
        const now = dayjs()
        return now.isAfter(dayjs(quota.value.period_from)) && now.isBefore(dayjs(quota.value.period_to))
    })
    const quotaPeriodLabel = computed(() => {
        if (!quota.value?.period_from || !quota.value?.period_to) return ''
        // period_to is exclusive → show the last included day.
        return `${formatDate(quota.value.period_from)} – ${formatDate(dayjs(quota.value.period_to).subtract(1, 'day').toISOString())}`
    })

    // --- range selection over the slot grid ---
    const startIdx = computed(() => (form.start ? slots.value.findIndex((s) => s.start === form.start) : -1))
    const endIdx = computed(() => (form.end ? slots.value.findIndex((s) => s.end === form.end) : -1))

    // Every grid that can contribute to the picked span. Out-of-grid night gaps simply have
    // no slots, which is exactly why they cost nothing (api-contract §5.2 multi-day).
    const spanGrids = computed<ISlot[]>(() => {
        if (!multiDay.value || endSelectedDate.value === selectedDate.value) return slots.value
        return [slots.value, ...Object.values(middleGrids.value), endSlots.value].flat()
    })

    // Slots the picked span fully covers — the quota unit of account on every day it touches.
    const coveredSlots = computed<ISlot[]>(() => {
        if (!form.start || !form.end) return []
        const from = dayjs(form.start).valueOf()
        const to = dayjs(form.end).valueOf()
        return spanGrids.value.filter(
            (s) => dayjs(s.start).valueOf() >= from && dayjs(s.end).valueOf() <= to,
        )
    })

    const selectedCost = computed(() => coveredSlots.value.reduce((sum, s) => sum + s.cost, 0))

    // Occupied / closed slots inside the span — the server would reject these anyway.
    const spanConflict = computed(() =>
        !previewTruncated.value && coveredSlots.value.some((s) => s.closed || !s.available),
    )

    // Multi-slot range whose SUM exceeds the remaining quota for the window. A truncated
    // preview misses the middle days, so the sum is a floor, not the real cost: it may
    // still prove the span is over budget, but never that it fits.
    const overQuota = computed(() =>
        quotaRemaining.value !== null && selectedCost.value > 0 && selectedCost.value > quotaRemaining.value,
    )

    // --- probes (v1.5: mandatory pick, defaulted to the effective probe at the slot) ---
    const compatibleProbes = computed(() =>
        probes.value.filter((p) => selectedDevice.value?.compatible_probe_ids.includes(p.id)),
    )
    const probeRadioItems = computed(() =>
        compatibleProbes.value.map((p) => ({ label: `${p.name}${p.code ? ` (${p.code})` : ''}`, value: p.id })),
    )
    // A hand-picked probe different from the effective one means the reservation waits for admin confirmation.
    const probeRequiresConfirm = computed(() =>
        !!form.probe_id && !!effectiveProbe.value.probe_id && form.probe_id !== effectiveProbe.value.probe_id,
    )

    // --- review helpers ---
    const selectedGroupName = computed(() => availableGroups.value.find((g) => g.id === form.group_id)?.name ?? '')
    const selectedProjectName = computed(() => projects.value.find((p) => p.id === form.project_id)?.name ?? '')
    const isCustomProject = computed(() => form.project_id === CUSTOM_VALUE)
    const reviewProject = computed(() =>
        isCustomProject.value ? form.custom_project : selectedProjectName.value,
    )

    // v1.7 free-reservation window: role `user` booking beyond the device's window creates a
    // pending reservation (auto-confirmed once the start enters the window). Never blocking.
    const freeWindowDays = computed(() => selectedDevice.value?.free_window_days ?? 0)
    const outsideWindow = computed(() => {
        if (isAdmin.value || !freeWindowDays.value || !form.start) return false
        // Anchor "now + N days" in the institute timezone to match the server's derivation.
        return dayjs(form.start).isAfter(dayjs().tz(TZ).add(freeWindowDays.value, 'day'))
    })
    const reviewProbe = computed(() => probes.value.find((p) => p.id === form.probe_id)?.name ?? '')
    const reviewRecurrence = computed(() => {
        if (!form.recurrenceEnabled) return t('wizard.no_recurrence')
        return `${t(`wizard.freq.${form.recType}`)} × ${form.recCount}`
    })

    // Per-step Zod schema — UForm validation is the gate that advances/submits the wizard.
    const stepSchema = computed<z.ZodType>(() => {
        if (step.value === 0) return z.object({
            group_id:       z.string().min(1, t('validation.required')),
            device_id:      z.string().min(1, t('validation.required')),
            custom_project: z.string().max(200, t('validation.max_length', { n: 200 })),
            note:           z.string().max(2000, t('validation.max_length', { n: 2000 })),
        }).superRefine((value, ctx) => {
            if (form.project_id === CUSTOM_VALUE && !value.custom_project.trim()) {
                ctx.addIssue({ code: 'custom', path: ['custom_project'], message: t('validation.required') })
            }
        })
        if (step.value === 1) return z.object({
            start: z.string().min(1, t('validation.required')),
            end:   z.string().min(1, t('validation.required')),
        }).superRefine((_, ctx) => {
            // Block advancing when a single booking's range cost exceeds the remaining
            // quota. Recurrences may legitimately span windows, so the server stays
            // authoritative there (it silently skips over-quota occurrences).
            if (overQuota.value && !form.recurrenceEnabled) {
                ctx.addIssue({ code: 'custom', message: t('wizard.quota_over'), path: ['start'] })
            }
            if (form.start && form.end && dayjs(form.end).valueOf() <= dayjs(form.start).valueOf()) {
                ctx.addIssue({ code: 'custom', message: t('validation.time_order'), path: ['start'] })
            }
            // Occupied/closed slots inside the span are a hard stop while we can see them;
            // beyond the preview cap the server is the authority.
            if (spanConflict.value) {
                ctx.addIssue({ code: 'custom', message: t('wizard.span_conflict'), path: ['start'] })
            }
        })
        if (step.value === 2) return z.object({
            probe_id: z.string().min(1, t('validation.required')),
        })
        return z.object({})
    })

    // Fires only when the current step's schema validates.
    const onStepSubmit = () => {
        if (step.value < 3) step.value++
        else submit()
    }

    // --- actions ---
    const selectDevice = (d: IDevice) => {
        form.device_id = d.id
        form.start = ''
        form.end = ''
        form.probe_id = ''
        manualProbe.value = false
        effectiveProbe.value = { probe_id: null, probe_name: null }
    }

    // Fetch the effective probe at the selected slot start and default the pick to it.
    // Keep a manual pick if it is still compatible; otherwise re-default.
    const refreshEffectiveProbe = async () => {
        if (!form.device_id || !form.start) {
            effectiveProbe.value = { probe_id: null, probe_name: null }
            return
        }
        const { statusCode, data } = await fetchStore
            .fetch(`/devices/${form.device_id}/effective-probe?at=${encodeURIComponent(form.start)}`)
            .get().json<IEffectiveProbe>()
        effectiveProbe.value = statusCode.value === 200 && data.value
            ? data.value
            : { probe_id: null, probe_name: null }

        const compatible = compatibleProbes.value.some((p) => p.id === form.probe_id)
        if (!manualProbe.value || !compatible) {
            form.probe_id = effectiveProbe.value.probe_id ?? ''
            manualProbe.value = false
        }
    }

    watch(() => form.probe_id, () => {
        // Mark manual when the user picks something other than the current effective default.
        if (form.probe_id && form.probe_id !== effectiveProbe.value.probe_id) manualProbe.value = true
    })

    // v1.10 overnight slot: the day's last non-closed slot holds the machine until the next
    // day's first non-closed slot. Absent on every other slot (and on contiguous 24h grids).
    const overnightUntil = (s: ISlot): string =>
        s.overnight && s.overnight_until ? dayjs(s.overnight_until).tz(TZ).format('HH:mm') : ''

    // The banner is about the CURRENT pick, not the day: it appears only while the selected
    // range ENDS on the overnight slot (that slot alone, or a range finishing on it).
    const selectedOvernightUntil = computed(() => {
        if (!form.start || !form.end) return ''
        const slot = spanGrids.value.find((s) => s.overnight && s.overnight_until && s.end === form.end)
        return slot ? overnightUntil(slot) : ''
    })

    // Click = set start (single slot). Click a later slot = extend the range up to it,
    // provided every covered slot is available. Clicking inside the selection clears it;
    // clicking before the start restarts the selection there.
    const selectSlot = (start: string) => {
        const clickedIdx = slots.value.findIndex((s) => s.start === start)
        if (clickedIdx < 0) return

        // Clicking anywhere inside the current selection clears it.
        if (startIdx.value >= 0 && endIdx.value >= 0 && clickedIdx >= startIdx.value && clickedIdx <= endIdx.value) {
            form.start = ''
            form.end = ''
            return
        }

        if (startIdx.value >= 0 && clickedIdx > startIdx.value) {
            const allFree = slots.value.slice(startIdx.value, clickedIdx + 1).every((s) => s.available)
            if (allFree) {
                form.end = slots.value[clickedIdx].end
                return
            }
        }

        // Start a fresh single-slot selection.
        form.start = start
        form.end = slots.value[clickedIdx].end
    }

    // Multi-day: the left grid fixes the span start, the right grid its end. Picking a start
    // after the current end (or on a later day) drops the stale end.
    const selectSpanStart = (slot: ISlot) => {
        form.start = slot.start
        if (form.end && dayjs(form.end).valueOf() <= dayjs(slot.start).valueOf()) form.end = ''
    }

    const selectSpanEnd = (slot: ISlot) => {
        form.end = slot.end
        if (form.start && dayjs(form.start).valueOf() >= dayjs(slot.end).valueOf()) form.start = ''
    }

    // Re-evaluate the effective probe whenever the chosen slot start changes.
    watch(() => form.start, refreshEffectiveProbe)

    const fetchDayGrid = async (date: string): Promise<ISlotsResponse | null> => {
        const params = new URLSearchParams({ date })
        if (form.group_id) params.set('group_id', form.group_id)
        const { statusCode, data } = await fetchStore
            .fetch(`/devices/${form.device_id}/slots?${params.toString()}`)
            .get().json<ISlotsResponse>()
        return statusCode.value === 200 && data.value ? data.value : null
    }

    const loadEndSlots = async () => {
        if (!multiDay.value || !form.device_id || !endSelectedDate.value) {
            endSlots.value = []
            return
        }
        if (endSelectedDate.value === selectedDate.value) {
            // Same day on both sides — reuse the left grid so a slot is never fetched twice.
            endSlots.value = slots.value
            return
        }
        endSlotsLoading.value = true
        const grid = await fetchDayGrid(endSelectedDate.value)
        endSlots.value = grid?.slots ?? []
        endSlotsLoading.value = false
    }

    // Days strictly between the endpoints — needed only for the cost/conflict preview.
    const loadMiddleGrids = async () => {
        middleGrids.value = {}
        if (!multiDay.value || !form.device_id || spanDays.value <= 2 || previewTruncated.value) return
        const days: string[] = []
        let cursor = dayjs(selectedDate.value).add(1, 'day')
        const last = dayjs(endSelectedDate.value)
        while (cursor.isBefore(last, 'day')) {
            days.push(cursor.format('YYYY-MM-DD'))
            cursor = cursor.add(1, 'day')
        }
        const grids = await Promise.all(days.map((d) => fetchDayGrid(d)))
        const next: Record<string, ISlot[]> = {}
        days.forEach((d, i) => { next[d] = grids[i]?.slots ?? [] })
        middleGrids.value = next
    }

    const loadSlots = async () => {
        if (!form.device_id || !selectedDate.value) return
        slotsLoading.value = true
        const grid = await fetchDayGrid(selectedDate.value)
        if (grid) {
            slots.value = grid.slots ?? []
            // v1.3: quota for the window containing the selected date comes with the slots.
            if (grid.quota !== undefined && grid.quota !== null) {
                quota.value = grid.quota
            } else {
                await loadQuotaFallback()
            }
        } else {
            slots.value = []
            quota.value = null
        }
        slotsLoading.value = false
    }

    // Fallback for pre-v1.3 API that doesn't return quota in the slots payload.
    const loadQuotaFallback = async () => {
        quota.value = null
        if (!form.group_id || !form.device_id) return
        const { statusCode, data } = await fetchStore
            .fetch(`/group-quotas?group_id=${form.group_id}&device_id=${form.device_id}`)
            .get().json<IGroupQuota[]>()
        if (statusCode.value === 200 && data.value && data.value.length) {
            quota.value = data.value[0]
        }
    }

    watch([() => form.device_id, () => form.group_id, selectedDate], () => {
        if (step.value >= 1) loadSlots()
    })

    // Changing a day invalidates the pick made on that day's grid.
    watch(selectedDate, () => {
        if (!multiDay.value) return
        form.start = ''
        // The end day can never precede the start day.
        if (dayjs(endSelectedDate.value).isBefore(dayjs(selectedDate.value), 'day')) {
            endCalendarDate.value = calendarDate.value
            form.end = ''
        }
    })
    watch(endSelectedDate, () => {
        if (multiDay.value) form.end = ''
    })

    watch(multiDay, (on) => {
        if (on) {
            // Start the end picker on the start day; the end slot must be chosen explicitly.
            endCalendarDate.value = calendarDate.value
            form.end = ''
        } else {
            // Back to one day: keep the start slot only when it belongs to the visible grid.
            const slot = slots.value.find((s) => s.start === form.start)
            form.start = slot ? slot.start : ''
            form.end = slot ? slot.end : ''
            endSlots.value = []
            middleGrids.value = {}
        }
    })

    watch([multiDay, endSelectedDate, selectedDate, () => form.device_id, () => form.group_id, slots], () => {
        if (step.value >= 1) loadEndSlots()
    })
    watch([multiDay, endSelectedDate, selectedDate, () => form.device_id, () => form.group_id], () => {
        if (step.value >= 1) loadMiddleGrids()
    })

    watch(step, (s) => {
        errorMessage.value = ''
        if (s === 1) loadSlots()
    })

    // --- data loading on open ---
    // v1.12: the project list is scoped to the chosen group (the backend also drops
    // archived rows), so it is re-queried whenever the group changes.
    const loadProjects = async () => {
        const params = new URLSearchParams({ per_page: '0' })
        if (form.group_id) params.set('group_id', form.group_id)
        const { statusCode, data } = await fetchStore
            .fetch(`/projects?${params.toString()}`).get().json<ListResponse<IProject>>()
        projects.value = statusCode.value === 200 && data.value ? data.value.items ?? [] : []
        // Drop a pick the refreshed list no longer offers (free text is unaffected).
        if (form.project_id !== NONE_VALUE && form.project_id !== CUSTOM_VALUE
            && !projects.value.some((p) => p.id === form.project_id)) {
            form.project_id = NONE_VALUE
        }
    }

    const loadReferenceData = async () => {
        const [d, g, pr] = await Promise.all([
            fetchStore.fetch('/devices').get().json<IDevice[]>(),
            fetchStore.fetch('/groups').get().json<IGroup[]>(),
            fetchStore.fetch('/probes').get().json<IProbe[]>(),
        ])
        devices.value = d.data.value ?? []
        groups.value = g.data.value ?? []
        probes.value = pr.data.value ?? []
        await loadProjects()
    }

    watch(() => form.group_id, loadProjects)

    const applyPrefill = () => {
        form.group_id = appStore.userData?.primary_group_id ?? availableGroups.value[0]?.id ?? ''
        if (prefill.value.device_id) form.device_id = prefill.value.device_id
        if (prefill.value.date) calendarDate.value = isoToCalendarDate(prefill.value.date) ?? todayCalendarDate()
        if (prefill.value.start) {
            form.start = prefill.value.start
        }
    }

    watch(open, async (isOpen) => {
        if (isOpen) {
            step.value = 0
            errorMessage.value = ''
            await loadReferenceData()
            applyPrefill()
        }
    })

    const reset = () => {
        step.value = 0
        form.group_id = ''
        form.project_id = NONE_VALUE
        form.custom_project = ''
        form.note = ''
        form.device_id = ''
        form.start = ''
        form.end = ''
        form.probe_id = ''
        form.recurrenceEnabled = false
        form.recType = 'weekly'
        form.recCount = 4
        manualProbe.value = false
        effectiveProbe.value = { probe_id: null, probe_name: null }
        errorMessage.value = ''
        slots.value = []
        multiDay.value = false
        endSlots.value = []
        middleGrids.value = {}
        quota.value = null
        wizardStore.closeWizard()
    }

    const submit = async () => {
        submitting.value = true
        errorMessage.value = ''
        const payload = {
            device_id:  form.device_id,
            start_at:   form.start,
            end_at:     form.end,
            probe_id:   form.probe_id,
            group_id:   form.group_id || null,
            project_id: isCustomProject.value || form.project_id === NONE_VALUE ? null : form.project_id,
            custom_project: isCustomProject.value ? form.custom_project.trim() : '',
            note:       form.note.trim(),
            recurrence: form.recurrenceEnabled ? { type: form.recType, count: form.recCount } : null,
        }
        const { statusCode, data } = await fetchStore
            .fetch('/reservations').post(payload).json<IReservationCreateResponse>()

        if (statusCode.value === 201 && data.value) {
            const count = data.value.count
            // Client can tell a pending reservation: the chosen probe differs from the
            // effective one, or the start lies beyond the device's free window (v1.7).
            const message = probeRequiresConfirm.value || outsideWindow.value
                ? t('wizard.created_pending')
                : count > 1 ? t('wizard.created_many', { n: count }) : t('wizard.created_one')
            PrintSuccess(message)
            wizardStore.notifyChanged()
            open.value = false
        } else {
            errorMessage.value = parseError(data.value, t('errors.on_add'))
        }
        submitting.value = false
    }
</script>
